import Phaser from "phaser";
import Camera = Phaser.Cameras.Scene2D.Camera;
import InputPlugin = Phaser.Input.InputPlugin;
import EventEmitter = Phaser.Events.EventEmitter;

const DRAG_THRESHOLD = 5;

interface EventMap {
  'zoom': { delta: number };
  'click': { worldX: number, worldY: number };
  'mouseover': { worldX: number, worldY: number }
  'error': Error;
}

export class InputManager {
  private _input: InputPlugin;
  private _camera: Camera;
  private _emitter = new EventEmitter();

  private isLeftDown = false;
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private cameraStartX = 0;
  private cameraStartY = 0;

  private _activePointers = new Map<number, { x: number; y: number }>();
  private _isPinching = false;
  private _pinchLastDistance = 0;

  public dragEnabled: boolean = true;

  cancelDrag(): void {
    this.isLeftDown = false;
    this.isDragging = false;
    this._isPinching = false;
    this._activePointers.clear();
  }

  constructor(input: InputPlugin, camera: Camera) {
    this._input = input;
    this._camera = camera;

    this._input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: any[], _deltaX: number, deltaY: number, _deltaZ: number) => {
      const delta = deltaY > 0 ? 0.95 : 1.05;
      this.emit('zoom', { delta });
    });

    this._input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this._activePointers.set(pointer.id, { x: pointer.x, y: pointer.y });

      if (this._activePointers.size === 2) {
        this._isPinching = true;
        this._pinchLastDistance = this.getPinchDistance();
        this.isLeftDown = false;
        this.isDragging = false;
        return;
      }

      if (pointer.leftButtonDown()) {
        this.isLeftDown = true;
        this.dragStartX = pointer.x;
        this.dragStartY = pointer.y;
        this.cameraStartX = camera.scrollX;
        this.cameraStartY = camera.scrollY;
      }
    });

    this._input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this._activePointers.has(pointer.id)) {
        this._activePointers.set(pointer.id, { x: pointer.x, y: pointer.y });
      }

      if (this._isPinching) {
        const newDist = this.getPinchDistance();
        if (this._pinchLastDistance > 0) {
          this.emit('zoom', { delta: newDist / this._pinchLastDistance });
        }
        this._pinchLastDistance = newDist;
        return;
      }

      if (this.isLeftDown && !this.isDragging) {
        const dragDeltaX = pointer.x - this.dragStartX;
        const dragDeltaY = pointer.y - this.dragStartY;
        if (Math.abs(dragDeltaX) > DRAG_THRESHOLD || Math.abs(dragDeltaY) > DRAG_THRESHOLD) {
          this.isDragging = true;
        }
      }

      if (this.isDragging && this.dragEnabled) {
        // Calculate the drag offset in screen space
        const dragDeltaX = pointer.x - this.dragStartX;
        const dragDeltaY = pointer.y - this.dragStartY;

        // Convert to world space and apply to camera (inverted to make map "stick" to mouse)
        const newScrollX = this.cameraStartX - dragDeltaX / camera.zoom;
        const newScrollY = this.cameraStartY - dragDeltaY / camera.zoom;

        this._camera.setScroll(newScrollX, newScrollY);
      } else {
        this.emit('mouseover', { worldX: pointer.worldX, worldY: pointer.worldY })
      }
    });

    this._input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this._activePointers.delete(pointer.id);

      if (this._isPinching) {
        if (this._activePointers.size < 2) {
          this._isPinching = false;
          this._pinchLastDistance = 0;
        }
        return;
      }

      if (pointer.leftButtonReleased()) {
        this.isLeftDown = false;
        if (!this.isDragging) {
          this.emit('click', { worldX: pointer.worldX, worldY: pointer.worldY });
        }
        this.isDragging = false;
      }
    });
  }

  private getPinchDistance(): number {
    const pts = [...this._activePointers.values()];
    if (pts.length < 2) return 0;
    const dx = pts[1].x - pts[0].x;
    const dy = pts[1].y - pts[0].y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Method to subscribe to an event
  on<TEventName extends keyof EventMap & string>(
    eventName: TEventName,
    listener: (payload: EventMap[TEventName]) => void
  ): void {
    this._emitter.on(eventName, listener as (...args: any[]) => void);
  }

  // Method to unsubscribe from an event
  off<TEventName extends keyof EventMap & string>(
    eventName: TEventName,
    listener: (payload: EventMap[TEventName]) => void
  ): void {
    this._emitter.off(eventName, listener as (...args: any[]) => void);
  }

  // Method to emit an event
  emit<TEventName extends keyof EventMap & string>(
    eventName: TEventName,
    payload: EventMap[TEventName]
  ): boolean {
    return this._emitter.emit(eventName, payload);
  }
}